import React, { useEffect, useState } from 'react';
import { apiService } from './services/api'; 
import './style/PageStyle/SessionManager.css'; 

const DeviceManager = () => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Modal State ---
    const [showModal, setShowModal] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [isRevoking, setIsRevoking] = useState(false);

    // --- Cleanup State ---
    const [isCleaning, setIsCleaning] = useState(false);
    const [cleanupMessage, setCleanupMessage] = useState(null);

    const fetchDevices = async () => {
        try {
            const data = await apiService.getDevices();
            setDevices(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // 1. User clicks "Revoke" -> Open Modal
    const handleRevokeClick = (device) => {
        setSelectedDevice(device);
        setShowModal(true);
    };

    // 2. User clicks "Yes" in Modal -> API Call
    const confirmRevoke = async () => {
        if (!selectedDevice) return;
        
        setIsRevoking(true);
        try {
            await apiService.deleteDevice(selectedDevice.id);
            // Remove from list locally
            setDevices(devices.filter(d => d.id !== selectedDevice.id));
            closeModal();
        } catch (err) {
            alert("Failed to revoke session");
            setIsRevoking(false);
        }
    };

    // 3. Manual Cleanup Trigger
    const handleCleanup = async () => {
        setIsCleaning(true);
        setCleanupMessage(null);
        try {
            const response = await apiService.cleanupSessions(); 
            setCleanupMessage(response.message || "Cleanup successful");
            // Refresh list to ensure we see only valid ones
            fetchDevices();
        } catch (err) {
            setCleanupMessage("Error running cleanup");
        } finally {
            setIsCleaning(false);
            // Clear message after 3 seconds
            setTimeout(() => setCleanupMessage(null), 3000);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedDevice(null);
        setIsRevoking(false);
    };

    useEffect(() => {
        fetchDevices();
    }, []);

    if (loading) return <div className="loading-state">Loading active sessions...</div>;

    return (
        <div className="device-manager-container">
            <div className="device-header">
                <h2>Active Sessions</h2>
                {/* NEW CLEANUP BUTTON */}
                <div className="header-actions">
                    {cleanupMessage && <span className="success-msg">{cleanupMessage}</span>}
                    <button 
                        className="cleanup-btn" 
                        onClick={handleCleanup} 
                        disabled={isCleaning}
                    >
                        {isCleaning ? 'Cleaning...' : 'Clean Expired Sessions'}
                    </button>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {devices.length === 0 && !error ? (
                <div className="empty-state">No active sessions found.</div>
            ) : (
                <div className="table-container">
                    <table className="device-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Device / OS</th>
                                <th>Browser</th>
                                <th>IP Address</th>
                                <th>Login Time</th>
                                <th>Expires At</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {devices.map((device) => (
                                <tr key={device.id}>
                                    <td className="user-cell">
                                        {device.username}
                                    </td>
                                    <td>
                                        <div className="device-info">
                                            <span className="device-type">{device.device_type}</span>
                                            <span className="device-os">{device.os}</span>
                                        </div>
                                    </td>
                                    <td>{device.browser}</td>
                                    <td>
                                        <span className="ip-address">{device.ip_address}</span>
                                    </td>
                                    <td className="time-cell">
                                        {new Date(device.login_time).toLocaleString()}
                                    </td>
                                    <td className="time-cell">
                                        {/* Display Expiry Time */}
                                        {device.expires_at ? new Date(device.expires_at).toLocaleString() : 'N/A'}
                                    </td>
                                    <td>
                                        <button 
                                            className="revoke-btn"
                                            onClick={() => handleRevokeClick(device)}
                                        >
                                            Revoke
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* --- CONFIRMATION MODAL --- */}
            {showModal && selectedDevice && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Revoke Session?</h3>
                        <p>
                            Are you sure you want to log out <strong>{selectedDevice.username}</strong>?
                        </p>
                        <div className="modal-details">
                            <p><span>Device:</span> {selectedDevice.os} - {selectedDevice.browser}</p>
                            <p><span>IP:</span> {selectedDevice.ip_address}</p>
                        </div>
                        <p className="warning-text">
                            The user will be forced to log in again immediately.
                        </p>
                        
                        <div className="modal-actions">
                            <button 
                                className="cancel-btn" 
                                onClick={closeModal}
                                disabled={isRevoking}
                            >
                                Cancel
                            </button>
                            <button 
                                className="confirm-btn" 
                                onClick={confirmRevoke}
                                disabled={isRevoking}
                            >
                                {isRevoking ? 'Revoking...' : 'Yes, Revoke'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeviceManager;
